import React, { useMemo, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, Info, Loader } from 'lucide-react';
import { aiAdmin } from '../services/api';
import { AnalyticsTabs } from '../components/AnalyticsPanels';
import { formatUsd } from '../components/AiProvidersManager';
import './Analytics.css';

/**
 * AI usage (admins): every request Virtual Labs sent to an AI model, with who
 * started it, for which lab, the tokens it used and what it cost, from the
 * ledger (backend services/AiUsageReport.js). A period (Qatar calendar days)
 * and filters narrow everything on the page; the requests table and the CSV
 * show the same rows. A second tab lists every change to the AI settings.
 */

export const PURPOSES = {
  'student-report': 'Student reports', 'class-report': 'Class reports', 'scene-captions': 'Scene captions', 'connection-test': 'Key tests',
};
const FEATURES = { 'lab-insights': 'Lab insights', settings: 'Settings' };
const PERIODS = [['month', 'This month'], ['last-month', 'Last month'], ['30d', 'Last 30 days'], ['custom', 'Between two dates']];
const RESULTS = {
  throttled: 'Throttled', server: 'Provider failing', refused: 'Refused', 'invalid-response': 'Not an answer', timeout: 'Timed out',
  cancelled: 'Cancelled', 'not-sent': 'Not sent', redirect: 'Redirect refused', 'too-large': 'Answer too large', network: 'Connection lost',
};
const ACTIONS = {
  'ai.credential.create': 'Added a key', 'ai.credential.update': 'Edited a key', 'ai.credential.replace-key': 'Replaced a key',
  'ai.credential.enable': 'Enabled a key', 'ai.credential.disable': 'Disabled a key', 'ai.credential.delete': 'Deleted a key',
  'ai.credential.reseal': 'Sealed a key again (new master key)', 'ai.settings.assign': 'Chose the keys Lab insights uses',
  'ai.settings.retention': 'Changed how long usage is kept',
};

const number = (n) => Number(n || 0).toLocaleString('en-US');
const compact = (n) => Number(n || 0).toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const when = (value) => new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const purposeOf = (row) => `${FEATURES[row.feature] || row.feature} · ${PURPOSES[row.purpose] || row.purpose}`;
const errorText = (error, fallback) => error?.response?.data?.error || fallback;

/** The query a set of filters makes: empty ones left out, dates only between two dates. */
function paramsOf(filters) {
  const params = { period: filters.period };
  if (filters.period === 'custom') Object.assign(params, { from: filters.from, to: filters.to });
  for (const key of ['credential', 'actor', 'lab', 'purpose']) if (filters[key]) params[key] = filters[key];
  return params;
}

function AiUsage() {
  const [tab, setTab] = useState('usage');
  return (
    <div className="analytics-page animate-fade-in" data-testid="ai-usage-page">
      <div className="analytics-header">
        <div>
          <h1>AI usage</h1>
          <p>Every request Virtual Labs sent to an AI model: who started it, for which lab, the tokens it used and what it cost.</p>
        </div>
      </div>
      <AnalyticsTabs tabs={[{ key: 'usage', label: 'Usage' }, { key: 'audit', label: 'Changes to AI settings' }]}
        value={tab} onChange={setTab} panelId="ai-usage-panel" label="AI usage" />
      <div id="ai-usage-panel" role="tabpanel" aria-labelledby={`ai-usage-panel-tab-${tab}`}>
        {tab === 'usage' ? <Usage /> : <AuditLog />}
      </div>
    </div>
  );
}

/* ---------------------------------- Usage ---------------------------------- */

function Usage() {
  const [filters, setFilters] = useState({ period: 'month', from: '', to: '', credential: '', actor: '', lab: '', purpose: '' });
  const [csvError, setCsvError] = useState('');
  const [exporting, setExporting] = useState(false);
  const params = useMemo(() => paramsOf(filters), [filters]);
  const ready = filters.period !== 'custom' || (!!filters.from && !!filters.to);
  const periodOnly = useMemo(() => paramsOf({ ...filters, credential: '', actor: '', lab: '', purpose: '' }), [filters]);

  const summary = useQuery({ queryKey: ['ai-usage', params], queryFn: () => aiAdmin.usage(params), enabled: ready, placeholderData: keepPreviousData });
  // The filters' choices: everything in the period, whatever is filtered.
  const choices = useQuery({ queryKey: ['ai-usage', periodOnly], queryFn: () => aiAdmin.usage(periodOnly), enabled: ready, placeholderData: keepPreviousData });
  const events = useInfiniteQuery({
    queryKey: ['ai-usage-events', params], enabled: ready, initialPageParam: undefined,
    queryFn: ({ pageParam }) => aiAdmin.usageEvents({ ...params, ...(pageParam ? { before: pageParam } : {}) }),
    getNextPageParam: (page) => page.next || undefined,
  });

  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const options = choices.data || summary.data;
  const keys = (options?.byModel || []).reduce((list, m) => {
    const value = m.credential || 'environment';
    if (!list.some((o) => o.value === value)) list.push({ value, label: m.source === 'environment' ? 'Environment key' : (m.credentialName || 'Key') });
    return list;
  }, []);

  async function exportCsv() {
    setCsvError('');
    setExporting(true);
    try {
      const blob = await aiAdmin.usageCsv(params);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-usage-${summary.data?.period?.from || 'period'}-to-${summary.data?.period?.to || ''}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      let message = 'The file could not be made.';
      try { message = JSON.parse(await error.response.data.text()).error || message; } catch { /* not JSON */ }
      setCsvError(message);
    } finally {
      setExporting(false);
    }
  }

  const data = summary.data;
  const totals = data?.totals;
  return (
    <>
      <div className="analytics-scope" role="group" aria-label="Which requests">
        <label>Period
          <select value={filters.period} onChange={(e) => set('period', e.target.value)} data-testid="ai-usage-period">
            {PERIODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        {filters.period === 'custom' && (
          <>
            <label>From<input type="date" value={filters.from} onChange={(e) => set('from', e.target.value)} data-testid="ai-usage-from" /></label>
            <label>To<input type="date" value={filters.to} onChange={(e) => set('to', e.target.value)} data-testid="ai-usage-to" /></label>
          </>
        )}
        <label>Key
          <select value={filters.credential} onChange={(e) => set('credential', e.target.value)} data-testid="ai-usage-key">
            <option value="">All keys</option>
            {keys.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label>Staff member
          <select value={filters.actor} onChange={(e) => set('actor', e.target.value)} data-testid="ai-usage-actor">
            <option value="">Everyone</option>
            {(options?.byActor || []).filter((a) => a.actor).map((a) => <option key={a.actor} value={a.actor}>{a.actorEmail || 'Unknown'}</option>)}
          </select>
        </label>
        <label>Lab
          <select value={filters.lab} onChange={(e) => set('lab', e.target.value)} data-testid="ai-usage-lab">
            <option value="">All labs</option>
            {(options?.byLab || []).filter((l) => l.lab).map((l) => <option key={l.lab} value={l.lab}>{l.labName || 'A lab'}</option>)}
          </select>
        </label>
        <label>Purpose
          <select value={filters.purpose} onChange={(e) => set('purpose', e.target.value)} data-testid="ai-usage-purpose">
            <option value="">All purposes</option>
            {(options?.byPurpose || []).map((p) => <option key={`${p.feature}/${p.purpose}`} value={p.purpose}>{purposeOf(p)}</option>)}
          </select>
        </label>
        <button type="button" className="btn btn-sm" onClick={exportCsv} disabled={!ready || exporting || !totals?.attempts}
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 38 }} data-testid="ai-usage-csv">
          {exporting ? <Loader size={14} className="spinner" aria-hidden="true" /> : <Download size={14} aria-hidden="true" />} Export CSV
        </button>
      </div>
      {csvError && <div role="alert" className="analytics-error">{csvError}</div>}

      {!ready ? <div className="analytics-empty">Choose the first and last day of the period.</div>
        : summary.isError ? <div role="alert" className="analytics-error">{errorText(summary.error, 'The AI usage could not be read.')}</div>
          : !data ? <div className="analytics-loading" role="status"><Loader size={18} className="spinner" aria-hidden="true" /> Reading the AI usage…</div>
            : (
              <>
                <div className="analytics-stats" data-testid="ai-usage-totals">
                  <div className="analytics-stat"><span>Cost</span><strong>{formatUsd(totals.costMicroUsd)}</strong>
                    <small>{totals.estimatedMicroUsd ? `of which up to ${formatUsd(totals.estimatedMicroUsd)} estimated` : 'at the prices each request was recorded with'}</small></div>
                  <div className="analytics-stat"><span>Requests</span><strong>{number(totals.calls)}</strong>
                    <small>{number(totals.attempts)} sent · {number(totals.failures)} failed</small></div>
                  <div className="analytics-stat"><span>Tokens read</span><strong>{compact(totals.inputTokens)}</strong>
                    <small>{compact(totals.cachedInputTokens)} from the cache · {compact(totals.cacheWriteTokens)} written to it · {number(totals.images)} images</small></div>
                  <div className="analytics-stat"><span>Tokens written</span><strong>{compact(totals.outputTokens)}</strong>
                    <small>{compact(totals.reasoningTokens)} of them reasoning</small></div>
                </div>
                {totals.unpriced > 0 && (
                  <p role="status" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'var(--accent-warning)', margin: '-6px 0 16px' }}>
                    <AlertTriangle size={14} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
                    <span>{number(totals.unpriced)} request{totals.unpriced === 1 ? '' : 's'} had no prices (the environment's key, or a deployment without prices): {totals.unpriced === 1 ? 'its' : 'their'} cost is not counted.</span>
                  </p>
                )}
                <SpendByDay days={data.byDay} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginBottom: 18 }}>
                  <Breakdown title="By staff member" rows={data.byActor} name={(r) => r.actorEmail || 'Unknown'} sub={(r) => r.actorRole} />
                  <Breakdown title="By lab" rows={data.byLab} name={(r) => r.labName || (r.lab ? 'A lab' : 'No lab (key tests)')} />
                  <Breakdown title="By purpose" rows={data.byPurpose} name={purposeOf} />
                  <Breakdown title="By model" rows={data.byModel} name={(r) => r.deployment || 'Unknown'}
                    sub={(r) => (r.source === 'environment' ? 'Environment key' : r.credentialName)} />
                </div>
              </>
            )}

      {ready && <Requests events={events} />}
      <Retention />
    </>
  );
}

/** Spend by day: one bar a day, keyboard and hover read out a day; a table shows the same. */
function SpendByDay({ days }) {
  const [selected, setSelected] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const max = Math.max(1, ...days.map((d) => d.costMicroUsd));
  const active = days[selected ?? days.length - 1];
  const width = Math.max(days.length * 12, 120);
  return (
    <section className="card analytics-chart" aria-labelledby="ai-spend-title" data-testid="ai-usage-by-day">
      <div className="analytics-chart-header">
        <h2 id="ai-spend-title">Spend by day</h2>
        <button type="button" className="btn btn-sm" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>{asTable ? 'Show the chart' : 'Show as a table'}</button>
      </div>
      {asTable ? (
        <div className="analytics-table-frame" style={{ maxHeight: 320 }}>
          <table className="analytics-table" aria-label="Spend by day">
            <thead><tr><th>Day</th><th>Requests</th><th>Tokens read</th><th>Tokens written</th><th>Cost</th></tr></thead>
            <tbody>{days.map((d) => (
              <tr key={d.day}><td>{d.day}</td><td>{number(d.calls)}</td><td>{number(d.inputTokens)}</td><td>{number(d.outputTokens)}</td><td>{formatUsd(d.costMicroUsd)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <>
          <svg viewBox={`0 0 ${width} 180`} preserveAspectRatio="none" role="img" tabIndex={0}
            aria-label="Cost per day, Qatar time. Use left and right arrows to read a day."
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
              event.preventDefault();
              setSelected((current) => Math.max(0, Math.min(days.length - 1, (current ?? days.length - 1) + (event.key === 'ArrowRight' ? 1 : -1))));
            }}>
            {[45, 90, 135, 175].map((y) => <line key={y} x1="0" x2="100%" y1={y} y2={y} stroke="var(--border-primary)" />)}
            {days.map((d, index) => {
              const height = d.costMicroUsd ? Math.max(2, (d.costMicroUsd / max) * 166) : 0;
              return (
                <g key={d.day} onMouseEnter={() => setSelected(index)}>
                  <rect x={index * 12} y={0} width="12" height="180" fill="transparent" />
                  {height > 0 && <rect x={index * 12 + 1.5} y={175 - height} width="9" height={height} rx="2" fill="var(--accent-primary)"
                    opacity={(selected ?? days.length - 1) === index ? 1 : 0.65} />}
                  <title>{`${d.day}: ${formatUsd(d.costMicroUsd)}, ${number(d.calls)} requests`}</title>
                </g>
              );
            })}
          </svg>
          <div className="analytics-chart-caption" role="status" data-testid="ai-usage-day-caption">
            {active ? `${active.day} · ${formatUsd(active.costMicroUsd)} · ${number(active.calls)} request${active.calls === 1 ? '' : 's'} · ${compact(active.inputTokens)} tokens read, ${compact(active.outputTokens)} written` : ''}
          </div>
        </>
      )}
    </section>
  );
}

function Breakdown({ title, rows, name, sub }) {
  const shown = rows.slice(0, 10);
  return (
    <section className="card" style={{ padding: 16, minWidth: 0 }} aria-label={title}>
      <h2 style={{ fontSize: 15, margin: '0 0 10px' }}>{title}</h2>
      {rows.length === 0 ? <p className="analytics-muted" style={{ margin: 0 }}>Nothing in this period.</p> : (
        <div className="analytics-table-frame" style={{ maxHeight: 'none' }}>
          <table className="analytics-table">
            <thead><tr><th>{title.replace(/^By /, '').replace(/^./, (c) => c.toUpperCase())}</th><th>Requests</th><th>Tokens</th><th>Cost</th></tr></thead>
            <tbody>{shown.map((r, i) => (
              <tr key={`${name(r)}|${sub ? sub(r) : ''}|${i}`}>
                <td style={{ whiteSpace: 'normal', minWidth: 140 }}>
                  <span className="analytics-ellipsis" style={{ maxWidth: 260 }}>{name(r)}</span>
                  {sub && sub(r) && <span className="analytics-muted">{sub(r)}</span>}
                </td>
                <td>{number(r.calls)}</td>
                <td title={`${number(r.inputTokens)} read, ${number(r.outputTokens)} written`}>{compact(r.inputTokens + r.outputTokens)}</td>
                <td>{formatUsd(r.costMicroUsd)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {rows.length > shown.length && <p className="analytics-muted" style={{ margin: '8px 0 0' }}>And {rows.length - shown.length} more (the CSV has them all).</p>}
    </section>
  );
}

function Requests({ events }) {
  const rows = (events.data?.pages || []).flatMap((page) => page.events);
  return (
    <section className="card" style={{ padding: 16, marginBottom: 18 }} aria-labelledby="ai-requests-title" data-testid="ai-usage-requests">
      <h2 id="ai-requests-title" style={{ fontSize: 15, margin: '0 0 10px' }}>Requests</h2>
      {events.isError ? <div role="alert" className="analytics-error">{errorText(events.error, 'The requests could not be read.')}</div>
        : events.isPending ? <div className="analytics-loading" role="status"><Loader size={18} className="spinner" aria-hidden="true" /> Reading the requests…</div>
          : rows.length === 0 ? <div className="analytics-empty">No requests in this period.</div> : (
            <div className="analytics-table-frame">
              <table className="analytics-table" aria-busy={events.isFetching}>
                <thead><tr><th>Time</th><th>Staff member</th><th>Lab</th><th>Purpose</th><th>Model</th><th>Tokens read</th><th>Tokens written</th><th>Cost</th><th>Time taken</th><th>Result</th></tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r.id}>
                    <td>{when(r.at)}{r.attempt > 1 && <span className="analytics-tag">try {r.attempt}</span>}</td>
                    <td><span className="analytics-ellipsis" style={{ maxWidth: 200 }}>{r.actorEmail || '—'}</span></td>
                    <td><span className="analytics-ellipsis" style={{ maxWidth: 220 }}>{r.labName || '—'}</span></td>
                    <td>{purposeOf(r)}</td>
                    <td><span className="analytics-mono">{r.deployment || '—'}</span><span className="analytics-muted">{r.source === 'environment' ? 'Environment key' : r.credentialName}</span></td>
                    <td>{number(r.tokens?.input)}</td>
                    <td>{number(r.tokens?.output)}</td>
                    <td title={r.estimated ? 'No usage came back: the most it could have cost' : undefined}>
                      {r.priced ? `${r.estimated ? '≈ ' : ''}${formatUsd(r.costMicroUsd)}` : <span className="analytics-muted">no prices</span>}
                    </td>
                    <td>{(r.latencyMs / 1000).toFixed(1)} s</td>
                    <td>{r.ok ? 'Answered' : `${RESULTS[r.errorClass] || 'Failed'}${r.httpStatus ? ` (${r.httpStatus})` : ''}`}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
      {events.hasNextPage && (
        <div className="analytics-pager">
          <span>{number(rows.length)} shown, newest first</span>
          <button type="button" className="btn btn-sm" onClick={() => events.fetchNextPage()} disabled={events.isFetchingNextPage}>
            {events.isFetchingNextPage ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}
    </section>
  );
}

/** How long the ledger keeps its rows. */
function Retention() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['ai-admin'], queryFn: aiAdmin.get, retry: false });
  const [editing, setEditing] = useState(false);
  const [days, setDays] = useState('');
  const save = useMutation({
    mutationFn: () => aiAdmin.setRetention({ revision: settings.data.settings.revision, retentionDays: parseInt(days, 10) }),
    onSuccess: () => { setEditing(false); queryClient.invalidateQueries({ queryKey: ['ai-admin'] }); },
  });
  const current = settings.data?.settings?.retentionDays;
  if (!current) return null;
  return (
    <div className="analytics-freshness" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }} data-testid="ai-usage-retention">
      <Info size={13} aria-hidden="true" />
      {editing ? (
        <>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Keep usage records for
            <input type="number" min={30} max={3650} value={days} onChange={(e) => setDays(e.target.value)} style={{ width: 90, padding: '4px 8px' }} data-testid="ai-usage-retention-days" /> days
          </label>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => save.mutate()} disabled={save.isPending}>Save</button>
          <button type="button" className="btn btn-sm" onClick={() => { setEditing(false); save.reset(); }} disabled={save.isPending}>Cancel</button>
          {save.isError && <span role="alert" style={{ color: 'var(--accent-danger)' }}>{errorText(save.error, 'Not saved.')}</span>}
        </>
      ) : (
        <>
          <span>Usage records are kept for {number(current)} days ({(current / 365).toFixed(current % 365 ? 1 : 0)} years), then deleted.</span>
          <button type="button" className="analytics-link" onClick={() => { setDays(String(current)); setEditing(true); }}>Change</button>
        </>
      )}
    </div>
  );
}

/* ---------------------------------- Audit ---------------------------------- */

const valueText = (value) => (value === null || value === undefined ? '—'
  : typeof value === 'object' ? JSON.stringify(value) : String(value));

function AuditLog() {
  const log = useInfiniteQuery({
    queryKey: ['ai-audit'], initialPageParam: undefined,
    queryFn: ({ pageParam }) => aiAdmin.audit(pageParam ? { before: pageParam } : {}),
    getNextPageParam: (page) => page.next || undefined,
  });
  const rows = (log.data?.pages || []).flatMap((page) => page.events);
  return (
    <section data-testid="ai-audit">
      <p className="analytics-muted" style={{ fontSize: 13, marginTop: 0 }}>
        Who added, edited, disabled or deleted a key, changed prices or budgets, or chose the keys a feature uses. A key itself never appears here, only its fingerprint.
      </p>
      {log.isError ? <div role="alert" className="analytics-error">{errorText(log.error, 'The audit log could not be read.')}</div>
        : log.isPending ? <div className="analytics-loading" role="status"><Loader size={18} className="spinner" aria-hidden="true" /> Reading the audit log…</div>
          : rows.length === 0 ? <div className="analytics-empty">No changes yet.</div> : (
            <div className="analytics-table-frame">
              <table className="analytics-table" aria-label="Changes to AI settings">
                <thead><tr><th>Time</th><th>Who</th><th>What</th><th>Of</th><th>Changed</th></tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r.id}>
                    <td>{when(r.at)}</td>
                    <td>{r.actorEmail || 'The platform'}</td>
                    <td style={{ textAlign: 'left' }}>{ACTIONS[r.action] || r.action}</td>
                    <td style={{ textAlign: 'left' }}>{r.target?.name || '—'}</td>
                    <td style={{ textAlign: 'left', whiteSpace: 'normal', minWidth: 220 }}>
                      {r.changed?.length ? (
                        <details>
                          <summary style={{ cursor: 'pointer' }}>{r.changed.join(', ')}</summary>
                          <dl style={{ margin: '6px 0 0', fontSize: 11.5, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px' }}>
                            {r.changed.filter((field) => field !== 'key').map((field) => (
                              <React.Fragment key={field}>
                                <dt className="analytics-muted">{field}</dt>
                                <dd style={{ margin: 0, wordBreak: 'break-word' }} className="analytics-mono">{valueText(r.before?.[field])} → {valueText(r.after?.[field])}</dd>
                              </React.Fragment>
                            ))}
                          </dl>
                        </details>
                      ) : '—'}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
      {log.hasNextPage && (
        <div className="analytics-pager">
          <span>{number(rows.length)} shown, newest first</span>
          <button type="button" className="btn btn-sm" onClick={() => log.fetchNextPage()} disabled={log.isFetchingNextPage}>
            {log.isFetchingNextPage ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}
    </section>
  );
}

export default AiUsage;
